
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { DatePicker } from "@/components/ui/date-picker"
import { countries } from '@/data/countries';

interface PersonalInformationCardProps {
  formData: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address: string;
    country: string;
    birth_date: string;
    clothing_size: string;
    emailChanged: boolean;
  };
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDateChange: (date: Date | undefined) => void;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

export function PersonalInformationCard({ 
  formData, 
  handleChange, 
  handleDateChange,
  setFormData 
}: PersonalInformationCardProps) {
  const { t } = useTranslation(["clients", "common"]);


  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('personalInformation', { ns: 'clients', defaultValue: 'Personal Information' })}</CardTitle>
        <CardDescription>{t('updatePersonalInformation', { ns: 'clients', defaultValue: 'Update your personal information' })}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firstName">{t('firstName', { ns: 'clients', defaultValue: 'First Name' })}</Label>
            <Input
              type="text"
              id="firstName"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
            />
          </div>
          <div>
            <Label htmlFor="lastName">{t('lastName', { ns: 'clients', defaultValue: 'Last Name' })}</Label>
            <Input
              type="text"
              id="lastName"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email">{t('email', { ns: 'clients', defaultValue: 'Email' })}</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="bg-background text-foreground"
            />
            {formData.email && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('profile.emailHint', { ns: 'clients', defaultValue: 'This changes the display email only, not the auth email' })}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="phone">{t('phone', { ns: 'clients', defaultValue: 'Phone' })}</Label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="address">{t('address', { ns: 'clients', defaultValue: 'Address' })}</Label>
          <Input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="country">{t('country', { ns: 'clients', defaultValue: 'Country' })}</Label>
            <Select
              name="country"
              onValueChange={(value) => setFormData(prevData => ({ ...prevData, country: value }))}
              defaultValue={formData.country}
              value={formData.country}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('selectCountry', { ns: 'clients', defaultValue: 'Select a country' })} />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.value} value={country.value}>
                    {country.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="birthDate">{t('birthDate', { ns: 'clients', defaultValue: 'Birth Date' })}</Label>
            <DatePicker
              date={formData.birth_date ? new Date(formData.birth_date) : undefined}
              setDate={handleDateChange}
              placeholder={t('pickDate', { ns: 'clients', defaultValue: 'Pick a date' })}
              allowDeselect={true}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('profile.birthDateOptional', { ns: 'clients', defaultValue: 'Birth date is optional' })}
            </p>
          </div>
        </div>

        <div>
          <Label htmlFor="clothingSize">{t('profile.clothingSize', { ns: 'clients', defaultValue: 'Clothing Size' })}</Label>
          <Input
            type="text"
            id="clothingSize"
            name="clothing_size"
            value={formData.clothing_size}
            onChange={handleChange}
          />
        </div>
      </CardContent>
    </Card>
  );
}
